'use client';

import {
  amplitudeSendEvent,
  GTM_NOTSET_INTEGER,
  GTM_NOTSET_STRING,
  getGtmAttr,
  getScreenName,
} from '@musinsa/frontend-utils';
import { Button, IcArrowDown, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import type { MyCurationGoodsItem } from '@musinsa/services-curator';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { curatorQueries } from '../../query-factory';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import { MyCurationProduct } from './MyCurationProduct';

interface Props {
  title: string;
  curationId: string;
  items: MyCurationGoodsItem[];
  pageCache: Map<string, number>;
  setPageCache: React.Dispatch<React.SetStateAction<Map<string, number>>>;
  curationIndex: number;
  defaultPage?: number;
}

const MIN_COUNT = 6;

const myCurationGoodsToAmplitudeProduct = (item: MyCurationGoodsItem) => {
  return {
    product_id: item.goodsNo,
    product_name: item.goodsName,
    price: item.price,
    discount_rate: item.salesRate,
    brand_name: item.brandName,
  };
};

export const MyShopGoodItems = ({
  title,
  curationId,
  items,
  pageCache,
  setPageCache,
  curationIndex,
  defaultPage = 1,
}: Props) => {
  const { service } = useDeviceInfo();
  const currentPage = pageCache.get(curationId) || defaultPage;

  const { data: userInfo } = useQuery({
    ...curatorQueries.profile.userInfo,
  });
  const curatorSnsTitle = userInfo?.snsTitle ?? '';

  const showMore = useMemo(
    () => items.length > MIN_COUNT * currentPage,
    [items.length, currentPage],
  );

  const onLoadMore = useCallback(() => {
    if (service === SERVICE.MUSINSA && curatorSnsTitle) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'curation_more',
        component_name: 'more_button',
        event_name: 'click_more_button',
        from: getScreenName(),
        id: curatorSnsTitle,
        section_name: 'curator_curationfeed',
        type: 'goods',
      });
    }
    const nextPage = currentPage + 1;
    setPageCache((prev) => new Map(prev).set(curationId, nextPage));
  }, [curationId, currentPage, curatorSnsTitle, service, setPageCache]);

  return (
    <div>
      <div className="grid grid-cols-3 w-full bg-neutral-color-white">
        {[...items].slice(0, MIN_COUNT * currentPage).map((item, index) => {
          const gtmAttr =
            service === SERVICE.MUSINSA
              ? getGtmAttr(['select_item', 'view_item_list'], {
                  discount:
                    item.normalPrice != null && item.price != null
                      ? item.normalPrice - item.price
                      : GTM_NOTSET_INTEGER,
                  discount_rate: item.salesRate?.toString() ?? GTM_NOTSET_STRING,
                  item_brand: item.brandName ?? GTM_NOTSET_STRING,
                  item_category: GTM_NOTSET_STRING,
                  item_id: String(item.goodsNo),
                  item_list_id: GTM_NOTSET_STRING,
                  item_list_index: String(index),
                  original_price: item.normalPrice ?? GTM_NOTSET_INTEGER,
                  price: item.price ?? GTM_NOTSET_INTEGER,
                  quantity: 1,
                  ...(item.couponPrice != null ? { best_price: item.couponPrice } : {}),
                })
              : undefined;

          return (
            <MyCurationProduct
              key={item.goodsNo}
              item={item}
              index={index}
              enableDeeplink
              callbackClick={() => {
                if (service === SERVICE['29CM']) {
                  track({
                    schema: SNOWPLOW_SCHEME.CLICK_ITEM,
                    data: {
                      page_name: 'curator_shop_manage',
                      section_name: `curation-${curationId}`,
                      section_title: title,
                      position: curationIndex + 1,
                      item_position: index + 1,
                      item_no: item.goodsNo,
                      item_name: item.goodsName,
                      brand_no: -1,
                      brand_name: item.brandName,
                      large_category_no: -1,
                      large_category_name: '',
                      middle_category_no: -1,
                      middle_category_name: '',
                      small_category_no: -1,
                      small_category_name: '',
                    },
                  });
                }
                if (service === SERVICE.MUSINSA && curatorSnsTitle) {
                  const product = myCurationGoodsToAmplitudeProduct(item);
                  amplitudeSendEvent({
                    action_name: 'click',
                    component_name: 'product',
                    event_name: 'click_product',
                    from: getScreenName(),
                    id: curatorSnsTitle,
                    index,
                    section_name: 'curator_curationfeed',
                    type: 'goods',
                    ...product,
                  });
                }
              }}
              callbackImpression={() => {
                if (service !== SERVICE['29CM']) return;
                track({
                  schema: SNOWPLOW_SCHEME.IMPRESSION_ITEM,
                  data: {
                    page_name: 'curator_shop_manage',
                    section_name: `curation-${curationId}`,
                    section_title: title,
                    position: curationIndex + 1,
                    item_position: index + 1,
                    item_no: item.goodsNo,
                    item_name: item.goodsName,
                    brand_no: -1,
                    brand_name: item.brandName,
                    large_category_no: -1,
                    large_category_name: '',
                    middle_category_no: -1,
                    middle_category_name: '',
                    small_category_no: -1,
                    small_category_name: '',
                  },
                });
              }}
              onClickLikeButton={(_item, nextIsLiked) => {
                if (service === SERVICE['29CM']) {
                  track({
                    schema: SNOWPLOW_SCHEME.LIKE_ITEM,
                    data: {
                      page_name: 'curator_shop_manage',
                      section_name: `curation-${curationId}`,
                      section_title: title,
                      position: curationIndex + 1,
                      item_position: index + 1,
                      item_no: item.goodsNo,
                      item_name: item.goodsName,
                    },
                  });
                }
                if (service === SERVICE.MUSINSA && curatorSnsTitle) {
                  const product = myCurationGoodsToAmplitudeProduct(item);
                  amplitudeSendEvent({
                    action_name: nextIsLiked ? 'like_product' : 'unlike_product',
                    component_name: 'product',
                    event_name: nextIsLiked ? 'like_product_product' : 'unlike_product_product',
                    from: getScreenName(),
                    id: curatorSnsTitle,
                    index,
                    section_name: 'curator_curationfeed',
                    type: 'goods',
                    ...product,
                  });
                }
              }}
              {...gtmAttr}
            />
          );
        })}
      </div>
      {showMore && (
        <div className="flex justify-center px-4 py-3">
          <Button
            size={40}
            variant="tertiaryOutlinedGray"
            className="flex items-center gap-[2px] w-full"
            onClick={onLoadMore}
          >
            <Typography variant="body14pxMed">
              더보기({MIN_COUNT * currentPage}/{items.length}개)
            </Typography>
            <IcArrowDown width="16px" height="16px" />
          </Button>
        </div>
      )}
    </div>
  );
};
